import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AUTH_LOGIN_ROUTE_SEGMENTS } from '@tix/auth/constants';
import { UserActions, UserPartialState, UserSelector } from '@tix/auth/state';
import * as AuthActions from '@tix/auth/state/actions';
import * as AuthSelectors from '@tix/auth/state/selectors';
import {
  TixAddress,
  TixBusinessCodeGroup,
  TixGetContactByEmailAddressGQL,
  TixPhoneNumber
} from '@tix/data-access';
import { User } from '@tix/shared/models';
import {
  BUSINESS_CODE_GROUP_NAMES,
  TixBusinessGroupsService
} from '@tix/shared/state';
import {
  ComponentCanDeactivate,
  FileUploadDialogComponent,
  TixAddressEditComponent,
  TixDialog,
  TixPhoneNumbersComponent
} from '@tix/shared/ui/components';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { TixStaffService } from '@tix/staff/state';
import { selectEditMode } from 'libs/layout/src/lib/state/shared/shared.selectors';
import { SharedState } from 'libs/layout/src/lib/state/shared/shared.reducers';
import { toggleEditMode } from 'libs/layout/src/lib/state/shared/shared.actions';

function mustMatch(
  controlName: string,
  matchingControlName: string
): ValidatorFn {
  return (formControl: AbstractControl): ValidationErrors | null => {
    const formGroup: FormGroup = formControl as FormGroup;
    const control = formGroup.controls[controlName];
    const matchingControl = formGroup.controls[matchingControlName];

    if (matchingControl.errors && !matchingControl.errors.mustmatch) {
      return null;
    }

    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ mustmatch: true });
    } else {
      matchingControl.setErrors(null);
    }
    return null;
  };
}

@Component({
  selector: 'tix-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property
  host: {
    class: 'tix-register'
  }
})
export class TixRegisterComponent
  implements OnInit, OnDestroy, ComponentCanDeactivate
{
  userId$ = this.store.select(UserSelector.getAuthenticatedUser).pipe(
    map(u => u?.uid),
    take(1)
  );

  readonly LOGIN_ROUTE = ['/', ...AUTH_LOGIN_ROUTE_SEGMENTS];

  readonly businessPhoneTypes$ =
    this.businessGroupsService.getBusinessGroupByName(
      BUSINESS_CODE_GROUP_NAMES.CONTACT_PHONE_TYPE
    );
  readonly countryCode$ = this.businessGroupsService.getBusinessGroupByName(
    BUSINESS_CODE_GROUP_NAMES.COUNTRY_CODE
  );
  readonly statesCode$ = this.businessGroupsService.getBusinessGroupByName(
    BUSINESS_CODE_GROUP_NAMES.STATE_PROVINCE
  );

  readonly userInfoLoading$: Observable<boolean | undefined> =
    this.store.select(UserSelector.getUserLoaddingInfo);
  readonly isSavingPhoneNumbers$ = this.staffService.isSavingStaffPhoneNumbers$;
  readonly userLoaddingAddress$: Observable<boolean | undefined> =
    this.store.select(UserSelector.getUserLoaddingAddress);

  private isInitialUserLoad = true;
  isLoading = false;

  readonly userState$: Observable<User | null> = this.store
    .select(UserSelector.getAuthenticatedUser)
    .pipe(
      mergeMap(user => {
        return this.getContactByEmailAddress
          .fetch({
            emailAddress: user?.email
          })
          .pipe(
            map(data => {
              if (!this.isInitialUserLoad || user?.profilePicture) {
                return user;
              }
              this.isInitialUserLoad = false;
              return {
                ...user,
                profilePicture: data.data.Contact?.[0]?.profilePicture || ''
              } as User;
            }),
            catchError(() => of(user))
          );
      }),
      tap(user => {
        this.user = user;
        this.userId = this.user?.uid ?? '';
        this.registerForm.patchValue({ ...user });
      })
    );

  isEditMode: boolean;

  private destroyed$ = new Subject();

  readonly userPhoneNumbers$: Observable<TixPhoneNumber[]> = this.store
    .select(UserSelector.getUserPhoneNumbers)
    .pipe(
      map((phoneList: any) => {
        return (
          phoneList &&
          phoneList.map((phone: any) => {
            return {
              countryCode: phone.countryCode,
              type: phone.type,
              phoneNumberId: phone.phoneNumberId,
              phoneNumber: phone.phoneNumber
            };
          })
        );
      }),
      tap(phoneList => {
        if (phoneList && phoneList.length && !this.isEditMode) this.step = 3;
      })
    );

  readonly userAddress$: Observable<any[]> = this.store
    .select(UserSelector.getUserAddress)
    .pipe(
      tap(address => {
        if (address?.addressId) this.next(true);
      }),
      map(address => {
        return [
          {
            address: {
              addressId: address?.addressId,
              contactAddressId: address?.contactAddressId,
              contactId: address?.contactId,
              streetAddress: address?.streetAddress,
              suiteApartment: address?.suiteApartment,
              city: address?.city,
              stateProvince: address?.stateProvince,
              postalCode: address?.postalCode,
              countryCode: address?.countryCode
            }
          }
        ];
      })
    );

  registerAttempted = false;

  step = 1;

  user: User | null;
  userId: string;

  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  error: Observable<string>;

  registerForm = new FormGroup({
    email: new FormControl(null, [Validators.required, Validators.email]),
    password: new FormControl(null, [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(256)
    ]),
    confirmPassword: new FormControl(null, Validators.required),
    firstName: new FormControl(null, Validators.required),
    lastName: new FormControl(null, Validators.required),
    dateOfBirth: new FormControl(),
    profilePicture: new FormControl('')
  });

  @ViewChild(TixPhoneNumbersComponent)
  phoneNumbersComp?: TixPhoneNumbersComponent;
  @ViewChild(TixAddressEditComponent) addressEditComp?: TixAddressEditComponent;

  canDeactivate() {
    if (
      (this.isEditMode && this.registerForm.dirty) ||
      this.phoneNumbersComp?.phoneNumbersForm.dirty ||
      this.addressEditComp?.addressForm.dirty
    ) {
      return false;
    }

    return true;
  }

  ngOnInit(): void {
    this.shared.select(selectEditMode).subscribe({
      next: mode => (this.isEditMode = mode)
    });

    // Check if this is a company profile route
    this.route.data.subscribe(data => {
      if (data['isCompanyProfile']) {
        this.isEditMode = true;
        this.step = 1; // Start with the profile information step
        this.shared.dispatch(toggleEditMode({ mode: true }));
      }
    });

    this.userState$.pipe(takeUntil(this.destroyed$)).subscribe(user => {
      if (!this.isEditMode) {
        // If component is registartion just need to get userId and update the step to phone number wich is 2
        if (user?.uid) this.step = 2;
      } else {
        // If component is for updating logged-in user's details then patch user's state value in form(specially on refresh need to manage)
        this.registerForm.patchValue({ ...user });
        this.registerForm.markAsPristine();
      }
      this.user = user;
      this.userId = this.user?.uid ?? '';
    });

    this.route.params.subscribe(url => {
      if (url.step && url.step < 4 && url.step > 0) {
        //this.isEditMode = true;
        this.shared.dispatch(toggleEditMode({ mode: true }));
        this.step = +url.step;
      }
    });
  }

  get passwordLength(): number {
    return this.registerForm.get('password')?.value?.length ?? 0;
  }

  get phonesFormArray(): FormArray {
    return this.registerForm.get('phones') as FormArray;
  }

  constructor(
    private readonly store: Store<UserPartialState>,
    private readonly shared: Store<SharedState>,
    private readonly route: ActivatedRoute,
    private router: Router,
    private dialog: TixDialog,
    private getContactByEmailAddress: TixGetContactByEmailAddressGQL,
    private businessGroupsService: TixBusinessGroupsService,
    private staffService: TixStaffService
  ) {
    this.error = this.store
      .select(AuthSelectors.getUserError)
      .pipe(map(res => (res ? res : '')));
  }

  hasError(control: string, error: string): boolean {
    return this.registerForm.get(control)?.hasError(error) ?? false;
  }

  isMatch(control: string, matchControl: string): void {
    let matchBoolean: Boolean =
      this.registerForm.get(control)?.value !==
      this.registerForm.get(matchControl)?.value;
    if (matchBoolean) {
      this.registerForm.get(matchControl)?.setErrors({ match: true });
    } else {
      this.registerForm.get(matchControl)?.setErrors(null);
    }
  }

  register(): void {
    this.isLoading = true;
    if (!this.registerForm.valid || this.userId) {
      return;
    }

    this.route.queryParams
      .pipe(
        take(1),
        map(params => params.r)
      )
      .subscribe(returnUrl => {
        this.store.dispatch(
          AuthActions.registerAction({
            credentials: {
              email: this.registerForm.value.email,
              password: this.registerForm.value.password,
              firstName: this.registerForm.value.firstName,
              lastName: this.registerForm.value.lastName,
              dateOfBirth: this.registerForm.value.dateOfBirth
            },
            returnUrl: returnUrl ?? '/'
          })
        );
        this.isLoading = false;
      });
  }

  updateUserInfo(): void {
    if (this.registerForm.pristine || !this.userId) return;
    const user = {
      contactId: this.userId,
      firstName: this.registerForm.value.firstName,
      lastName: this.registerForm.value.lastName,
      dateOfBirth: this.registerForm.value.dateOfBirth,
      profilePicture: this.registerForm.value.profilePicture
    };
    this.store.dispatch(UserActions.updateUserInfoAction({ user }));
  }

  next(skip: boolean): void {
    if (!skip || this.isEditMode) return;
    if (this.step === 3) {
      this.router.navigate(['/']);
    } else {
      this.step++;
    }
  }

  async savePhoneNumbers(phoneNumbers: TixPhoneNumber[]) {
    console.log(phoneNumbers);
    this.staffService
      .saveStaffMemberPhoneNumbers(
        this.userId,
        phoneNumbers.map(p => ({
          countryCode: p.countryCode || '',
          phoneNumber: p.phoneNumber || '',
          type: p.type || ''
        }))
      )
      .toPromise();
    this.phoneNumbersComp?.phoneNumbersForm.markAsPristine();
  }

  saveAddress(address: TixAddress): void {
    this.store.dispatch(
      UserActions.saveUserAddress({ address, userId: this.userId })
    );
  }

  updateAddress(address: TixAddress): void {
    this.store.dispatch(
      UserActions.saveUserAddress({ address, userId: this.userId })
    );
  }

  deletePhoneNumber(phoneNumber: TixPhoneNumber): void {
    // this.store.dispatch(UserActions.deleteContactPhoneNumber({ phoneNumber }));
  }

  openUploadDialog() {
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '800px',
      data: {}
    });
    dialogRef.componentInstance.imageHeight = 200;
    dialogRef.componentInstance.imageWidth = 200;

    dialogRef.afterClosed().subscribe(files => {
      if (files) {
        this.registerForm.controls['profilePicture'].setValue(files[0] || '');
        this.registerForm.markAsDirty();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
